import { useEffect, useState, type FormEvent } from 'react';
import { ErrorBanner, TextField, useT, type ErrorEnvelopeView } from '@skm/ui';
import { EnrolmentDetails, SavedCodesCheck } from './Enrolment.tsx';
import { AuthLayout } from './AuthLayout.tsx';
import { errorView } from './errors.ts';
import { inviteTokenFromHash, type Auth, type Enrolment } from './session.ts';

/** Mirrors AUTH_POLICY (packages/platform auth/policy.ts) for the hint only; the API decides. */
const PASSWORD = { min: 12, max: 128 } as const;

export interface AcceptInviteProps {
  readonly auth: Auth;
  /** After the recovery codes were acknowledged: go to sign-in. */
  readonly onDone: () => void;
}

/**
 * /accept-invite [02 §7 Invite; CR-W2a-42 Q4]: the one-use token arrives in the URL fragment (never sent to a server
 * by the browser) and is removed from the address bar at once; it travels only in the POST body. The user sets a
 * password, then sees the authenticator key and recovery codes once, then signs in.
 */
export function AcceptInvite({ auth, onDone }: AcceptInviteProps) {
  const t = useT();
  const [token] = useState(() =>
    typeof location === 'undefined' ? null : inviteTokenFromHash(location.hash),
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<ErrorEnvelopeView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof location !== 'undefined' && location.hash)
      history.replaceState(null, '', location.pathname);
  }, []);

  if (!token)
    return (
      <AuthLayout>
        <main className="skm-signin">
          <h1>{t('frame.invite.title')}</h1>
          <div role="alert" className="skm-error">
            <p>{t('frame.invite.missingToken')}</p>
          </div>
        </main>
      </AuthLayout>
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError({ code: 'VALIDATION_FAILED', messageKey: 'frame.invite.mismatch' });
      return;
    }
    setBusy(true);
    auth
      .acceptInvite(token, password)
      .then(setEnrolment, (err: unknown) => setError(errorView(err)))
      .finally(() => {
        setPassword('');
        setConfirm('');
        setBusy(false);
      });
  };

  if (enrolment)
    return (
      <AuthLayout>
        <main className="skm-signin">
          <h1>{t('frame.enrol.title')}</h1>
          <EnrolmentDetails enrolment={enrolment} />
          <SavedCodesCheck checked={saved} onChange={setSaved} />
          <button type="button" disabled={!saved} onClick={onDone}>
            {t('frame.enrol.done')}
          </button>
        </main>
      </AuthLayout>
    );

  return (
    <AuthLayout>
      <main className="skm-signin">
        <h1>{t('frame.invite.title')}</h1>
        <p>{t('frame.invite.intro', PASSWORD)}</p>
        <ErrorBanner error={error} />
        <form onSubmit={submit}>
          <TextField
            label={t('frame.invite.password')}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            label={t('frame.invite.confirm')}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <button type="submit" disabled={busy}>
            {t('frame.invite.submit')}
          </button>
        </form>
      </main>
    </AuthLayout>
  );
}
